import { HttpClient } from '@angular/common/http';
import { AuthResourceService } from './auth-resource';
import { environment } from '@mri/environment';
import {
  AnyObject,
  AuthCache,
  CommonResponse,
  QueryParamsResource,
  ResponseList,
  ResponseSimple,
  ValueType
} from '@mri/types';
import { mergeMap, Observable } from 'rxjs';
import { jsonToUri } from '@mri/functions';
import { Socket, SocketIoConfig } from 'ngx-socket-io';

export interface BaseResourceProp {
  authSrv: AuthResourceService;
  http: HttpClient;
  baseUrl: string;
  isSocket?: boolean;
}

export class BaseResource <R>{

  private _apiUrl: string = environment.apiUrl;
  private _authSrcSrv: AuthResourceService;
  private _http: HttpClient;
  private _baseUrl: string;

  constructor(prop: BaseResourceProp) {
    this._baseUrl = prop.baseUrl;
    this._http = prop.http;
    this._baseUrl = prop.baseUrl;
    this._authSrcSrv = prop.authSrv;
  }

  listElements(query: QueryParamsResource): Observable<ResponseList<R>> {
    const baseUri: string = this._baseUrl;

    const { filter, paginate } = query;
    const params = [
      jsonToUri(filter || {}),
      jsonToUri(paginate as unknown as Record<string, ValueType | Array<ValueType>>, '$')
    ].filter(u => !!u.trim()).join('&');

    const url = [`${this._apiUrl}/${baseUri}/`, ...[params]].filter(u => !!u.trim()).join('?');
    return this._get<ResponseList<R>>(url);
  }

  countElements(query: QueryParamsResource): Observable<CommonResponse> {
    const baseUri: string = this._baseUrl;

    const { filter } = query;
    const params = [
      jsonToUri(filter ?? {})
    ].filter(u => !!u.trim()).join('&');

    const url = [`${this._apiUrl}/${baseUri}/count`, ...[params]].filter(u => !!u.trim()).join('?');
    return this._get<CommonResponse>(url);
  }

  getElement(id?: string): Observable<ResponseSimple<R>> {
    const url = [this._apiUrl, this._baseUrl, id].filter(v => !!v?.trim()).join('/');
    return this._get<ResponseSimple<R>>(url);
  }

  getElements(query: QueryParamsResource): Observable<ResponseList<R>> {
    const baseUri: string = this._baseUrl;

    const { filter, paginate } = query;
    const params = [
      jsonToUri(filter || {}),
      jsonToUri((paginate as unknown as Record<string, ValueType | Array<ValueType>>) || {}, '$')
    ].join('&');

    const url = [`${this._apiUrl}/${baseUri}`, params].filter(u => !!u.trim()).join('?');
    return this._get<ResponseList<R>>(url);
  }

  postElement(body: AnyObject): Observable<R> {
    const url = [`${this._apiUrl}/${(this._baseUrl)}`].filter(u => !!u.trim()).join('?');
    return this._post<R>(url, body);
  }

  putElement(body: R): Observable<R> {
    const url = [`${this._apiUrl}/${(this._baseUrl)}`].filter(u => !!u.trim()).join('?');
    return this._put<R>(url, body);
  }

  deleteElements(query: QueryParamsResource): Observable<ResponseList<R>> {
    const baseUri: string = this._baseUrl;

    const { filter } = query;
    const params = jsonToUri(filter || {});

    const url = [`${this._apiUrl}/${baseUri}/`, params].filter(u => !!u.trim()).join('?');
    return this._delete<ResponseList<R>>(url);
  }

  subscribeElement(id: string): Observable<R>{
    return this._startSocket(id);
  }

  private _get<T = AnyObject>(url: string) {
    return this._http.get<T>(url, { headers: this._authSrcSrv.header });
  }

  private _post<T = AnyObject>(url: string, body: AnyObject) {
    return this._http.post<T>(url, body, { headers: this._authSrcSrv.header });
  }

  private _put<T = AnyObject>(url: string, body: AnyObject) {
    return this._http.put<T>(url, body, { headers: this._authSrcSrv.header });
  }

  private _delete<T = AnyObject>(url: string) {
    return this._http.delete<T>(url, { headers: this._authSrcSrv.header });
  }

  private _startSocket(id: string): Observable<R>{
    return this._authSrcSrv.authFirst$
      .pipe(
        mergeMap((auth) => this._initSocket(auth as AuthCache, id))
      );
  }

  private _initSocket(auth: AuthCache, id: string): Observable<R> {
    const url = `${this._apiUrl}/${this._baseUrl}`;

    const config: SocketIoConfig = { url, options: { query: { token: auth.session.token } } };
    const socket = new Socket(config);
    socket.connect();
    socket.emit('subscribe', [id]);

    return socket.fromEvent<R>('element')

  }

}
